import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { useState } from "react";
import { MdArrowUpward, MdArrowDownward } from "react-icons/md";
import { icons } from "./CreateCategory";
import { getProductsByCategory, resetProductsCategory } from "interfaces/products";
import DeleteModal from "components/actions/Delete";
import { useDisclosure } from "@chakra-ui/hooks";

interface Category {
    id: string;
    name: string;
    icon: string;
    position?: number;
}

const CategoryManager = (props: {
    isOpen: any;
    onClose: any;
    categories: Category[];
    onCategoriesChange: (categories: Category[]) => void;
}) => {
    const { isOpen, onClose, categories, onCategoriesChange } = props;

    const [newCategoryName, setNewCategoryName] = useState("");
    const [newCategoryIcon, setNewCategoryIcon] = useState("none");
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [deleteModalData, setDeleteModalData] = useState<{category: Category, products: any[]} | null>(null);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

    const getCategoryId = (name: string) => {
        return name.toLowerCase().replace(/\s+/g, "-");
    };

    const addCategory = () => {
        if (!newCategoryName.trim()) return;

        const maxPosition = categories.length > 0 
            ? Math.max(...categories.map(c => c.position || 0))
            : -1;

        const newCategory: Category = {
            id: getCategoryId(newCategoryName),
            name: newCategoryName.trim(),
            icon: newCategoryIcon,
            position: maxPosition + 1,
        };

        const updatedCategories = [...categories, newCategory];
        onCategoriesChange(updatedCategories);
        setNewCategoryName("");
        setNewCategoryIcon("none");
    };

    const removeCategory = (id: string) => {
        const updatedCategories = categories.filter(c => c.id !== id);
        onCategoriesChange(updatedCategories);
    };

    const handleRemoveCategory = async (category: Category) => {
        try {
            // Vérifier si des produits utilisent cette catégorie
            const productsRes = await getProductsByCategory(category.id);
            
            if (productsRes && productsRes.data && productsRes.data.length > 0) {
                // Ouvrir le modal de confirmation avec les données
                setDeleteModalData({ category, products: productsRes.data });
                onDeleteModalOpen();
            } else {
                // Pas de produits, supprimer directement
                removeCategory(category.id);
            }
        } catch (error) {
            console.error('Erreur lors de la suppression de la catégorie:', error);
            alert('Une erreur est survenue lors de la suppression de la catégorie.');
        }
    };

    const confirmDelete = async () => {
        if (!deleteModalData) return;
        
        try {
            // Réinitialiser les produits
            await resetProductsCategory(deleteModalData.category.id);
            // Supprimer la catégorie
            removeCategory(deleteModalData.category.id);
            onDeleteModalClose();
            setDeleteModalData(null);
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            alert('Une erreur est survenue lors de la suppression de la catégorie.');
        }
    };

    const updateCategory = () => {
        if (!editingCategory || !editingCategory.name.trim()) return;

        const updatedCategories = categories.map(c =>
            c.id === editingCategory.id ? editingCategory : c
        );
        onCategoriesChange(updatedCategories);
        setEditingCategory(null);
    };

    const moveCategory = (categoryId: string, direction: 'up' | 'down') => {
        const sortedCategories = [...categories].sort((a, b) => (a.position || 0) - (b.position || 0));
        const currentIndex = sortedCategories.findIndex(c => c.id === categoryId);
        
        if (currentIndex === -1) return;
        if (direction === 'up' && currentIndex === 0) return;
        if (direction === 'down' && currentIndex === sortedCategories.length - 1) return;

        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        // Échanger les positions
        const updatedCategories = categories.map(c => {
            if (c.id === sortedCategories[currentIndex].id) {
                return { ...c, position: sortedCategories[swapIndex].position };
            }
            if (c.id === sortedCategories[swapIndex].id) {
                return { ...c, position: sortedCategories[currentIndex].position };
            }
            return c;
        });

        onCategoriesChange(updatedCategories);
    };

    const sortedCategories = [...categories].sort((a, b) => (a.position || 0) - (b.position || 0));

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[500px] !max-w-[85%] top-[8vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[750px] flex flex-col !z-[1004]">
                        <h4 className="text-lg font-bold mb-4">
                            Gérer les catégories
                        </h4>

                        {/* Add new category */}
                        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                            <h5 className="text-sm font-semibold mb-3">Ajouter une catégorie</h5>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <InputField
                                        label="Nom de la catégorie"
                                        placeholder="Ex: Pizza, Burger, Soupe..."
                                        id="categoryName"
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e: any) => setNewCategoryName(e.target.value)}
                                        inputSmall={true}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-navy-700 font-medium mb-2 block">
                                        Icône
                                    </label>
                                    <div className="grid grid-cols-7 gap-1">
                                        {icons.map((icon) => (
                                            <button
                                                key={icon.name}
                                                type="button"
                                                className={`border text-xl rounded px-1 py-1 cursor-pointer hover:bg-gray-100 ${
                                                    icon.name === newCategoryIcon && "bg-gray-300"
                                                }`}
                                                onClick={() => setNewCategoryIcon(icon.name)}
                                            >
                                                {icon.icon}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            className={`border text-xs py-1 rounded px-1 cursor-pointer hover:bg-gray-100 ${
                                                "none" === newCategoryIcon && "bg-gray-300"
                                            }`}
                                            onClick={() => setNewCategoryIcon("none")}
                                        >
                                            Aucun
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={addCategory}
                                    disabled={!newCategoryName.trim()}
                                    className="linear rounded-xl bg-toola-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Ajouter
                                </button>
                            </div>
                        </div>

                        {/* List existing categories */}
                        <div className="mb-4">
                            <h5 className="text-sm font-semibold mb-3">Catégories existantes</h5>
                            <div className="max-h-[300px] overflow-y-auto space-y-2">
                                {sortedCategories.map((category, index) => (
                                    <div
                                        key={category.id}
                                        className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg"
                                    >
                                        {editingCategory?.id === category.id ? (
                                            <div className="flex-1 flex flex-col gap-2">
                                                <input
                                                    type="text"
                                                    value={editingCategory.name}
                                                    onChange={(e) => setEditingCategory({
                                                        ...editingCategory,
                                                        name: e.target.value
                                                    })}
                                                    className="px-2 py-1 border border-blue-500 rounded text-sm focus:outline-none"
                                                />
                                                <div className="grid grid-cols-7 gap-1">
                                                    {icons.map((icon) => (
                                                        <button
                                                            key={icon.name}
                                                            type="button"
                                                            className={`border text-lg rounded px-1 cursor-pointer ${
                                                                icon.name === editingCategory.icon && "bg-gray-300"
                                                            }`}
                                                            onClick={() => setEditingCategory({
                                                                ...editingCategory,
                                                                icon: icon.name
                                                            })}
                                                        >
                                                            {icon.icon}
                                                        </button>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className={`border text-xs rounded px-1 cursor-pointer ${
                                                            "none" === editingCategory.icon && "bg-gray-300"
                                                        }`}
                                                        onClick={() => setEditingCategory({
                                                            ...editingCategory,
                                                            icon: "none"
                                                        })}
                                                    >
                                                        Aucun
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-sm">
                                                {category.icon !== 'none' && `${icons.find(i => i.name === category.icon)?.icon} `}
                                                {category.name}
                                            </span>
                                        )}
                                        <div className="flex gap-2">
                                            {editingCategory?.id !== category.id && (
                                                <>
                                                    <button
                                                        onClick={() => moveCategory(category.id, 'up')}
                                                        disabled={index === 0}
                                                        className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                                        title="Déplacer vers le haut"
                                                    >
                                                        <MdArrowUpward size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => moveCategory(category.id, 'down')}
                                                        disabled={index === sortedCategories.length - 1}
                                                        className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                                        title="Déplacer vers le bas"
                                                    >
                                                        <MdArrowDownward size={14} />
                                                    </button>
                                                </>
                                            )}
                                            {editingCategory?.id === category.id ? (
                                                <>
                                                    <button
                                                        onClick={updateCategory}
                                                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                                    >
                                                        ✓
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingCategory(null)}
                                                        className="text-xs px-2 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                                                    >
                                                        ✗
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => setEditingCategory(category)}
                                                        className="text-xs px-2 py-1 bg-toola-500 text-white rounded hover:bg-blue-700"
                                                    >
                                                        Modifier
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveCategory(category)}
                                                        className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                                    >
                                                        Supprimer
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {categories.length === 0 && (
                                    <p className="text-sm text-slate-500 text-center py-4">
                                        Aucune catégorie créée
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-5 flex gap-2 justify-end">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl bg-toola-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-toola-600"
                            >
                                Fermer
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
            
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    onDeleteModalClose();
                    setDeleteModalData(null);
                }}
                description={
                    deleteModalData 
                        ? `${deleteModalData.products.length} produit(s) utilisent cette catégorie : ${deleteModalData.products.slice(0, 5).map((p: any) => p.name).join(', ')}${deleteModalData.products.length > 5 ? ` et ${deleteModalData.products.length - 5} autre(s)...` : ''}. Si vous supprimez cette catégorie, ces produits seront déplacés vers "Produit vendable (sans catégorie)".`
                        : ''
                }
                context="une catégorie"
                deleteFunction={confirmDelete}
            />
        </Modal>
    );
};

export default CategoryManager;
